import { useCallback, useMemo, useState } from 'react';
import CurrencyFormat from 'react-currency-format';
import { CompetitorPriceDTO } from '../../../../../../../@types/IPA/GerenciadorDePrecos';
import { Competitors, PhotoModal } from '../../../../../../../components';
import { LoadingSpinerArea } from '../../../../../../../components/LoadingSpinner';
import { parseStringToPlural } from '../../../../../../../utils';
import { competitorLoadingArea, resolveRowIdentity } from '../../NegociacaoFornecedor.rowIdentity';
import { NegotiationDataItemProps } from '../../NegociacaoFornecedor.types';
import { CompetitorStoreList } from '../CompetitorStoreList';
import { hasCompetitorStoreList, hasCompetitorSummary, resolveCompetitionEmptyMessage } from './competitionSnapshot';

export interface IExpandedTableContent {
  rowData: NegotiationDataItemProps;
  /**
   * A área do indicador de carregamento desta linha, nomeada pela grade. Chega por propriedade porque
   * quem busca o detalhe nomeia a MESMA área, e as duas pontas têm de vir da mesma função -- nome
   * montado aqui com o código da loja produzia "undefined" na linha de rede.
   */
  loadingArea?: string;
}

export const ExpandedTableContent = ({ rowData, loadingArea }: IExpandedTableContent) => {
  const competitorsInfo = rowData.competitorsInfo;

  const summary = competitorsInfo?.competitor ?? competitorsInfo?.competitorInfoPanel;

  const showSummary = hasCompetitorSummary(summary);

  const showStoreList = hasCompetitorStoreList(competitorsInfo);

  const total = summary?.countCompetitorStore ?? 0;

  const [photoModal, setPhotoModal] = useState<CompetitorPriceDTO['photo']>(null);

  const showModal = useMemo(() => {
    return !!photoModal;
  }, [photoModal]);

  const handleHidePhotoModal = useCallback(() => {
    setPhotoModal(null);
  }, []);

  const handleClickPhotoModal = useCallback(
    (photo?: CompetitorPriceDTO['photo']) => {
      if (!photo) return;
      if (showModal) handleHidePhotoModal();
      else setPhotoModal(photo);
    },
    [showModal, handleHidePhotoModal],
  );

  return (
    <>
      <LoadingSpinerArea area={loadingArea ?? competitorLoadingArea(resolveRowIdentity(rowData))} height="100%" size="md" />
      <Competitors>
        {!showSummary && !showStoreList && <p className="competitor-empty">{resolveCompetitionEmptyMessage(competitorsInfo?.competitionSnapshotState)}</p>}

        {showSummary && (
          <Competitors.Section>
            <Competitors.Header>
              <Competitors.Icon icon="MdDescInsights" />
              <Competitors.Title>Concorrentes</Competitors.Title>
            </Competitors.Header>

            <Competitors.Content>
              <Competitors.Card>
                <Competitors.CardHeader>
                  <Competitors.Title>{competitorsInfo?.competitor ? 'Concorrência direta' : 'Concorrência Infopanel'}</Competitors.Title>
                  <Competitors.Subtitle>
                    Produto encontrado em{' '}
                    <strong>
                      {total} {parseStringToPlural('loja', total)}
                    </strong>
                  </Competitors.Subtitle>
                </Competitors.CardHeader>
                <Competitors.CardContent>
                  <Competitors.InfoBox isActive={rowData.competitivenessIndicator === 'RULE_CPI_AVG'}>
                    <Competitors.Value>
                      <CurrencyFormat
                        fixedDecimalScale
                        decimalScale={2}
                        value={summary.competitorAverage}
                        displayType="text"
                        prefix=" R$"
                        decimalSeparator=","
                        thousandSeparator="."
                      />
                    </Competitors.Value>
                    <Competitors.Label>
                      x̄ Preço
                      <br />
                      Médio
                    </Competitors.Label>
                  </Competitors.InfoBox>
                  <Competitors.InfoBox isActive={rowData.competitivenessIndicator === 'RULE_CPI_MIN'}>
                    <Competitors.Value>
                      <CurrencyFormat
                        fixedDecimalScale
                        decimalScale={2}
                        value={summary.competitorLower}
                        displayType="text"
                        prefix=" R$"
                        decimalSeparator=","
                        thousandSeparator="."
                      />
                    </Competitors.Value>
                    <Competitors.Label>
                      {'<'} Preço
                      <br />
                      Mais Baixo
                    </Competitors.Label>
                  </Competitors.InfoBox>
                  <Competitors.InfoBox isActive={rowData.competitivenessIndicator === 'RULE_CPI_MAX'}>
                    <Competitors.Value>
                      <CurrencyFormat
                        fixedDecimalScale
                        decimalScale={2}
                        value={summary.competitorHigher}
                        displayType="text"
                        prefix=" R$"
                        decimalSeparator=","
                        thousandSeparator="."
                      />
                    </Competitors.Value>
                    <Competitors.Label>
                      {'>'} Preço
                      <br />
                      Mais Alto
                    </Competitors.Label>
                  </Competitors.InfoBox>
                </Competitors.CardContent>
              </Competitors.Card>
            </Competitors.Content>
          </Competitors.Section>
        )}

        {showSummary && showStoreList && <Competitors.Separator />}

        {showStoreList && (
          <CompetitorStoreList
            competitorPrices={competitorsInfo?.competitorPriceDTO ?? []}
            retailPrice={rowData.retailPrice}
            onClickPhoto={handleClickPhotoModal}
          />
        )}
      </Competitors>
      <PhotoModal show={true} onHide={handleHidePhotoModal} photo={photoModal} />
    </>
  );
};
